import { Bindable, NotifyProperty, nameof } from "bindable-data";
import { IBuildingModel } from "./IBuildingModel";
import { IUpgradeBuildingModel } from "./upgrade/IUpgradeBuildingModel";
import { IBuildingRankUpModel } from "../../../popups/buildingRankUp/model/IBuildingRankUpModel";
import { IUserLevelUpModel } from "../../../popups/userlevelup/model/IUserLevelUpModel";
import { LandlordData } from "./LandlordData";
import { FixResult } from "./upgrade/FixResult";
import { UpgradeBuildingItemResultDto } from "../../../../networking/data/responses/upgrade/UpgradeBuildingItemResultDto";
import { UpgradeResult } from "./upgrade/UpgradeResult";
import { ILatestNewsModel } from "../latestnews/ILatestNewsModel";
import { FixBuildingItemResultDto } from "../../../../networking/data/responses/upgrade/FixBuildingItemResultDto";
import { PrepareMintResult } from "../../../popups/marketplace/main/models/building/PrepareMintResult";
import { PurchaseHistory } from "../../../popups/marketplace/main/models/building/PurchaseHistory";

export class BuildingFromNewsModel extends Bindable implements IBuildingModel {
    public readonly id: NotifyProperty<string>;
    public readonly address: NotifyProperty<string>;
    public readonly level: NotifyProperty<number>;
    public readonly nextLevel: NotifyProperty<number>;
    public readonly imageUrl: NotifyProperty<string>;
    public readonly area: NotifyProperty<number>;
    public readonly city: NotifyProperty<string>;
    public readonly costPerSqft: NotifyProperty<number>;
    public readonly sqft: NotifyProperty<number>;
    public readonly floors: NotifyProperty<number>;
    public readonly borough: NotifyProperty<string>;
    public readonly zipCode: NotifyProperty<string>;
    public readonly upgradePointsBalance: NotifyProperty<number>;
    public readonly requiredUpgradePoints: NotifyProperty<number>;
    public readonly visitCostInCoinsUpTo: NotifyProperty<number>;
    public readonly landlordData: NotifyProperty<LandlordData>;
    public readonly piggyClubNftMultiplier: NotifyProperty<number>;
    public readonly piggyClubImageUrl: NotifyProperty<string>;
    public readonly piggyMultiplier: NotifyProperty<number>;
    public readonly priceInUsd: NotifyProperty<number>;
    public readonly priceInEth: NotifyProperty<number>;
    public readonly marketplaceId: NotifyProperty<string>;
    public readonly tokenId: NotifyProperty<string>;
    public readonly minBuildingLevelToAddToMp: NotifyProperty<number>;
    public readonly buildingLevelUpModel: NotifyProperty<IBuildingRankUpModel>;
    public readonly userLevelUpModel: NotifyProperty<IUserLevelUpModel>;
    public readonly upgrades: Array<IUpgradeBuildingModel>;
    public readonly needToFix: NotifyProperty<boolean>;
    private upgradeFixResult: UpgradeResult | FixResult;

    public get upgradeOrFixResult(): UpgradeResult | FixResult {
        return this.upgradeFixResult;
    }

    public get canBeFixed(): boolean {
        return false;
    }

    get fromUpgrade(): boolean {
        return false;
    }

    public constructor(latestNewsModel: ILatestNewsModel, upgradeOrFixResult?: UpgradeResult | FixResult) {
        super();
        this.id = new NotifyProperty<string>(this, nameof(this, "id"), "");
        this.address = new NotifyProperty<string>(this, nameof(this, "address"), "");
        this.level = new NotifyProperty<number>(this, nameof(this, "level"), latestNewsModel.latestNewsInfo.value.buildingLevel);
        this.nextLevel = new NotifyProperty<number>(this, nameof(this, "nextLevel"), 0);
        this.imageUrl = new NotifyProperty<string>(this, nameof(this, "imageUrl"), "");
        this.area = new NotifyProperty<number>(this, nameof(this, "area"), 0);
        this.city = new NotifyProperty<string>(this, nameof(this, "city"), "");
        this.costPerSqft = new NotifyProperty<number>(this, nameof(this, "costPerSqft"), 0);
        this.sqft = new NotifyProperty<number>(this, nameof(this, "sqft"), 0);
        this.floors = new NotifyProperty<number>(this, nameof(this, "floors"), 0);
        this.borough = new NotifyProperty<string>(this, nameof(this, "borough"), "");
        this.zipCode = new NotifyProperty<string>(this, nameof(this, "zipCode"), "");
        this.upgradePointsBalance = new NotifyProperty<number>(this, nameof(this, "upgradePointsBalance"), 0);
        this.requiredUpgradePoints = new NotifyProperty<number>(this, nameof(this, "requiredUpgradePoints"), 0);
        this.visitCostInCoinsUpTo = new NotifyProperty<number>(this, nameof(this, "visitCostInCoinsUpTo"), 0);
        const landlord = latestNewsModel.latestNewsInfo.value.landlord != undefined ? new LandlordData({ avatarUrl: latestNewsModel.latestNewsInfo.value.landlord.avatar, inGameName: "guest" }) : undefined;
        this.piggyClubNftMultiplier = new NotifyProperty<number>(this, nameof(this, "piggyClubNftMultiplier"), null);
        this.piggyClubImageUrl = new NotifyProperty<string>(this, nameof(this, "piggyClubImageUrl"), null);
        this.piggyMultiplier = new NotifyProperty<number>(this, nameof(this, "piggyMultiplier"), latestNewsModel.latestNewsInfo.value.piggyMultiplier ?? 0);
        this.priceInUsd = new NotifyProperty<number>(this, nameof(this, "priceInUsd"), 0);
        this.priceInEth = new NotifyProperty<number>(this, nameof(this, "priceInEth"), 0);
        this.marketplaceId = new NotifyProperty<string>(this, nameof(this, "marketplaceId"), "");
        this.tokenId = new NotifyProperty<string>(this, nameof(this, "tokenId"), "");
        this.minBuildingLevelToAddToMp = new NotifyProperty<number>(this, nameof(this, "minBuildingLevelToAddToMp"), 0);
        this.landlordData = new NotifyProperty<LandlordData>(this, nameof(this, "landlordData"), landlord);
        this.buildingLevelUpModel = new NotifyProperty<IBuildingRankUpModel>(this, nameof(this, "buildingLevelUpModel"), undefined);
        this.userLevelUpModel = new NotifyProperty<IUserLevelUpModel>(this, nameof(this, "userLevelUpModel"), undefined);
        this.needToFix = new NotifyProperty<boolean>(this, nameof(this, "needToFix"), false);
        this.upgradeFixResult = upgradeOrFixResult;
        this.upgrades = undefined;
    }

    get purchaseHistory(): NotifyProperty<PurchaseHistory[]> {
        throw new Error("Method not implemented.");
    }

    public async prepareMint(): Promise<PrepareMintResult> {
        throw new Error("Not implemented.");
    }

    public async upgrade(_upgradeItemId: string): Promise<UpgradeBuildingItemResultDto> {
        throw new Error("Not implemented.");
    }

    public async fix(_fixItemId: string): Promise<FixBuildingItemResultDto> {
        throw new Error("Not implemented.");
    }
}
